import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import type { Persona } from '../lib/personas';

/**
 * Compact persona card for embedding in other pages.
 */
export function PersonaTip({ persona }: { persona: Persona }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <button
      onClick={() => setExpanded(!expanded)}
      className={`w-full rounded-xl border p-3 text-left transition-all ${persona.bg}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-lg">{persona.emoji}</span>
        <div className="flex-1 min-w-0">
          <span className={`text-xs font-semibold ${persona.color}`}>{persona.title}</span>
        </div>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 text-ink-tertiary" /> : <ChevronDown className="h-3.5 w-3.5 text-ink-tertiary" />}
      </div>
      {expanded && (
        <div className="mt-2 animate-fade-in">
          <p className="text-xs italic text-ink-secondary">"{persona.voice}"</p>
          <p className="mt-1.5 text-xs text-ink-tertiary">{persona.prompt}</p>
        </div>
      )}
    </button>
  );
}

/**
 * Section showing relevant personas for a theme.
 */
export function PersonaTips({
  personas,
  title = 'Who are you solving for?',
}: {
  personas: Persona[];
  title?: string;
}) {
  if (personas.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-tertiary">
          <Users className="h-3.5 w-3.5" />
          {title}
        </h3>
        <Link to="/personas" className="text-xs text-ink-tertiary hover:text-ink-secondary">
          All personas →
        </Link>
      </div>
      {personas.map((p) => (
        <PersonaTip key={p.id} persona={p} />
      ))}
    </div>
  );
}
